import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import { Link } from 'react-router-dom';
import AddNewScheduleData from './ScheduleNewAddModal';
import EditNoteModal from './EditNotes';
import CopyJobData from './ScheduleNewCopyJobModal';
import RescheduleJobData from './ScheduleNewRescheduleModal';
import JobInfoData from './ScheduleNewJobInfoModal';
import JobScheduleVoidModalData from './JobScheduleVoidModal';
import FlaggedJobsData from './ScheduleNewFlaggedJobsModal';
import ConfirmPopups from './ConfirmPopup';
import NotesPopup from './NotesPopup';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import authService from './api-authorization/AuthorizeService';

interface ScheduleDayProps {
    allowEdit: boolean;
    allowAllocateToPastDate: boolean;
    allowUpdateDayLimits: boolean;
    allowLock: boolean;
    allowGetAccountBalance: boolean;
    scheduleDay: ScheduleDay;
    scheduleBranchId: number;
    noteTypes: GenericList[];
    publicHolidays: PublicHoliday[];
    shutdownPeriods: ShutdownPeriod[];
    reasonOptions: GenericList[];
    scheduleTypes: GenericList[];
    scheduleWarningNo: number;
    scheduleBookedNo: number;
    loadingDay: boolean;    //used when refreshing the day
    updateScheduleDay(scheduleDate: string, scheduleDay: ScheduleDay): void;
    updateDay(scheduleDate: string, refreshDayOnly: boolean): void;
    saveDay(scheduleDate: string): void;
    saveDayOrder(scheduleDate: string, items: ItemOrder[]): void;
    goAllocate(scheduleDate: string): void;
    view(dateSelected: string, searchScheduleId: number): void;
}

interface ScheduleDayItem {
    showAddNew: boolean;
    //install: ScheduleItemDetails[];
    //pickup: ScheduleItemDetails[];
    showDayNotes: boolean;
    editDayNotes: EditDayNotes;
    jobOnly: boolean;
    jobIndex: number;
    itemIndex: number;
    showNotes: boolean;
    showCopy: boolean;
    onOffhireDate: string;
    copyItem: CopyScheduleItem;
    jobs: JobItem[],
    kwikstageJobs: KwikstageJobItem[],
    showReschedule: boolean;
    showHoliday: boolean;
    showCancel: boolean;
    cancelJobIndex: number;
    cancelIndex: number;
    showProformaInvoice: boolean;
    showJobInfo: boolean;
    showVoidGearList: boolean;
    scheduleId: number;
    jobQuoteItemId: number;
    showFlaggedJobs: boolean;
    errors: any;
    loading: boolean;
    loadingReckonData: boolean;
}

interface CopyScheduleItem {
    index: number;
    scheduleId: number;
    siteAddress: string;
    item: string;
    quantityScheduled: number;
    qtyType: string;
    unitOfMeasure: string;
    isKwikstage: boolean;
    kwikstageItems: KwikstageScheduleItem[];
    kwikstageOptions: KwikstageBayOptions[];
}

interface ItemOrder {
    scheduleId: number;
    jobOrder: number;
    rowVersionTimestamp: Uint8Array;
}

interface KwikstageScheduleItem {
    starterBay: string;
    lift: string;
    bays: number;
}

interface KwikstageBayOptions {
    starterBayId: number;
    starterBay: string;
    liftOptions: KwikstageLiftOptions[];
}

interface KwikstageLiftOptions {
    liftId: number;
    lift: string;
}

interface ScheduleDay {
    scheduledDate: string;
    scheduledDateActual: string;
    scheduledDateFormatted: string;
    scheduledDateFormattedSmall: string;
    lightDutyMins: string;
    voidMins: string;
    kwikstageTotal: string;
    notes: string;
    shortNotes: string;
    walkWayGuardRailPitchMax: number;
    walkWayGuardRailPitchMaxRemaining: number;
    voidMax: number;
    voidMaxRemaining: number;
    kwikstageMax: number;
    kwikstageMaxRemaining: number;
    dayAmountFormatted: string;
    status: string;
    statusId: number;
    isCurrentDay: boolean;
    jobs: ScheduleJobs[];
    scheduleErrors: string[];
}

interface ScheduleJobs {
    jobIndex: number;
    itemIndex: number;
    id: number;
    jobId: number;
    jobQuoteItemId: number;
    orderManual: string;
    siteAddress: string;
    customerId: number;
    customer: string;
    contact: string;
    siteContact: string;
    description: string;
    manualItem: boolean;
    type: string;
    typeId: number;
    childTypeId: number;    //used when jobs have child records for grid colours
    itemId: number;
    item: string;
    itemShortcut: string;
    itemIsKwikstage: boolean;
    itemIsVoid: boolean;
    quoteNumber: string;
    quotedAmountFormatted: string;
    daysHire: number;
    quantityTotal: string;  //quoted
    quantityScheduled: number;
    onOffhireDate: string;
    onOffhireDateFormatted: string;
    purchaseOrderNumber: string;
    accountTypeId: number;
    accountType: string;
    accountBalance: number;
    accountBalanceFormatted: string;
    creditLimit: number;
    remainingBalance: number;
    remainingBalanceFormatted: string;
    jobAmount: number;
    jobAmountFormatted: string;
    accountChecked: boolean;
    goAhead: boolean;
    pickupScheduled: boolean;
    callout: boolean;
    copy: boolean;
    showRedNotes: boolean;
    scheduleNotes: string;
    runsheetNotes: string;
    truckAccess: string;
    parking: string;
    siteClean: string;
    powerlines: string;
    showGreenNotes: boolean;
    itemStatusId: number;
    status: string;
    reckonUpdatesRequired: boolean;
    customerUpdatesRequired: boolean;
    overhireAllowed: boolean;
    unitOfMeasure: string;
    quantityType: string;
    bringForwardDate: string;
    bookedBy: string;
    planChanges: boolean;
    jobConfirmed: boolean;
    emailSent: boolean;
    jobOrder: number;
    allowEdit: boolean;
    allowDelete: boolean;
    allowExtend: boolean;
    allowInvoice: boolean;
    extraHours: number;
    items: ScheduleItems[];
    totalItems: number;
    itemQuantityType: string;
    showItems: boolean;
    isPlaceholder: boolean;
    rowVersionTimestamp: Uint8Array;
    isDirty: boolean;
}

interface ScheduleItems {
    jobIndex: number;
    itemIndex: number;
    id: number;
    jobId: number;
    jobQuoteItemId: number;
    orderManual: string;
    siteAddress: string;
    customerId: number;
    customer: string;
    contact: string;
    siteContact: string;
    description: string;
    manualItem: boolean;
    type: string;
    typeId: number;
    itemId: number;
    item: string;
    itemShortcut: string;
    itemIsKwikstage: boolean;
    quoteNumber: string;
    quotedAmountFormatted: string;
    daysHire: number;
    quantityTotal: string;  //quoted
    quantityScheduled: number;
    onOffhireDate: string;
    onOffhireDateFormatted: string;
    purchaseOrderNumber: string;
    accountTypeId: number;
    accountType: string;
    jobAmount: number;
    jobAmountFormatted: string;
    accountChecked: boolean;
    goAhead: boolean;
    pickupScheduled: boolean;
    callout: boolean;
    copy: boolean;
    showRedNotes: boolean;
    scheduleNotes: string;
    runsheetNotes: string;
    truckAccess: string;
    parking: string;
    siteClean: string;
    powerlines: string;
    showGreenNotes: boolean;
    itemStatusId: number;
    status: string;
    reckonUpdatesRequired: boolean;
    customerUpdatesRequired: boolean;
    overhireAllowed: boolean;
    unitOfMeasure: string;
    quantityType: string;
    bringForwardDate: string;
    bookedBy: string;
    planChanges: boolean;
    jobConfirmed: boolean;
    emailSent: boolean;
    jobOrder: number;
    allowEdit: boolean;
    allowDelete: boolean;
    allowExtend: boolean;
    allowInvoice: boolean;
    extraHours: number;
    rowVersionTimestamp: Uint8Array;
    isDirty: boolean;
}

interface ScheduleItemDetails {
    itemIndex: number;
    jobQuoteItemId: number;
    jobId: number;
    itemId: number;
    typeId: number;
    siteAddress: string;
    customer: string;
    item: string;
    status: string;
    quantityTotal: number;
    quantityScheduled: number;
    quantityType: string;
    hirePeriodActual: number;
    hirePeriod: string;
    accountTypeId: number;
    allowOnGoingHire: number;
    chargePublicHoliday: boolean;
    chargeShutdownPeriod: boolean;
    selected: boolean;
    scheduled: boolean;
    showDetail: boolean;
    callout: boolean;
    items: ScheduleRelatedItemDetails[];
    showRedNotes: boolean;
    notes: string;
}

interface ScheduleRelatedItemDetails {
    jobQuoteItemId: number;
    jobId: number;
    itemId: number;
    item: string;
    status: string;
    quantityTotal: number;
    quantityScheduled: number;
    quantityType: string;
    hirePeriod: string;
}

interface EditDayNotes {
    heading: string;
    rowIndex: number;
    parentIndex: number;
    id: number;
    itemId: number;
    item: string;
    notes: string;
}

interface GenericList {
    id: number;
    name: string;
}

interface AddNewResults {
    itemIndex: number;
    selected: boolean;
    scheduleId: number;
    scheduleDate: string;
    jobQuoteItemId: number;
    jobId: number;
    siteAddress: string;
    customer: string;
    item: string;
    itemShortcut: string;
    type: string;
    quantityTotal: number;
    quantityScheduled: number;
    quantityType: string;
    hirePeriod: string;
    onoffhireDate: string;
    pickupDate: string;
    bringForwardDate: string;
    bringForwardDateFormatted: string;
    pickupScheduled: boolean;
    callout: boolean;
    allowOnGoingHire: boolean;
    notes: string;
}

interface SaveItem {
    onOffhireDate: string
    quantity: number;
    dates: string[];
}

interface PublicHoliday {
    name: string;
    date: string;
}

interface ShutdownPeriod {
    startDate: string;
    endDate: string;
}

interface NewItem {
    jobId: number;
    siteAddress: string;
    customerName: string;
    contactName: string;
    scheduleDate: string;
    scheduleBranchId: number;
    description: string;
    typeId: number;
    notes: string;
    runsheetNotes: string;
    errors: any;
    subId: string;
}

interface JobItem {
    itemIndex: number;
    scheduleDate: string;
    quantity: number;
    warning: boolean;
    locked: boolean;
}

interface KwikstageJobItem {
    scheduleDate: string;
    jobQuoteItemCalculationId: number;
    starterBayId: number;
    liftId: number;
    bays: number;
    maxBays: number;
    baysTotal: number;
    warning: boolean;
    locked: boolean;
}

const WalkWayGuardRailPitchWarningNo: number = 4;
const VoidWarningNo: number = 3;
const KwikstageWarningNo: number = 250;

//ITEMS
const ItemRow = ({ itemIndex, jobIndex, item, scheduleDay, allowEdit, allowAllocateToPastDate, minDate, showInfo, onCellChange, onCellCheckboxChange, editNotes, copyItem, rescheduleItem, cancelItem, invoiceItem }: { itemIndex: number, jobIndex: number, item: ScheduleItems, scheduleDay: ScheduleDay, allowEdit: boolean, allowAllocateToPastDate: boolean, minDate: string, showInfo(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellChange(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellCheckboxChange(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, editNotes(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, copyItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, rescheduleItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, cancelItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, invoiceItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void }) => {
    return (
        <tr key={item.itemIndex} className={item.typeId === 1 ? "tableColours--install" : item.typeId === 2 ? "tableColours--pickup" : item.typeId === 3 ? "tableColours--assist" : item.typeId === 4 ? "tableColours--cleaning" : "tableColours--manual"}>
            <td className="hidden">{item.id}</td>
            <td className={"table__text--align table--col-xs " + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>{item.jobIndex}</td>
            <td className="table__text--align table--col-xs">
                <div>
                    <a href="#" onClick={(e) => showInfo(e, false, jobIndex, item.itemIndex)}>
                        <span className="fas fa-info-circle copy--icon alignIconCenter tooltipitem tooltipitem__right"><span className="tooltipitemtext tooltipitemtext__right">Booking Information</span></span>
                    </a>
                </div>
            </td>
            <td className={"table__text--align table--col-s " + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>
                {item.orderManual}
            </td>
            <td className={"table__text--align"}>
                <Link className={item.jobId == 0 ? "hidden" : (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")} to={'/job/' + item.jobId} target="_blank">
                    <div className="tooltipitem tooltipitem__right">{item.siteAddress.length > 30 ? item.siteAddress.substring(0, 30) + "... " : item.siteAddress}
                        <span className="tooltipitemtext tooltipitemtext__right">{item.siteAddress}</span>
                    </div>
                </Link>
                <div className={item.jobId != 0 ? "hidden" : "tooltipitem tooltipitem__right"}>{item.siteAddress ? item.siteAddress.substring(0, 20) + "... " : ""}
                    <span className="tooltipitemtext tooltipitemtext__right">{item.siteAddress}</span>
                </div>
            </td>
            <td className={"table__text--align"}>
                <Link className={item.customerId == 0 ? "hidden" : (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")} to={'/customer/' + item.customerId} target="_blank">
                    <div className="tooltipitem tooltipitem__right">{item.customer.length > 15 ? item.customer.substring(0, 15) + "... " : item.customer}
                        <span className="tooltipitemtext tooltipitemtext__right">{item.customer}</span>
                    </div>
                </Link>
            </td>
            <td className={"table__text--align table--col-s " + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <div className="tooltipitem tooltipitem__right">{item.manualItem ? item.description.substring(0, 5) + "... " : item.itemShortcut}
                    <span className="tooltipitemtext tooltipitemtext__right">{item.manualItem ? item.description : item.item}</span>
                </div>
            </td>
                <td className={"table__text--align table--cell--nowrap table--col-s "  + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <span className="tooltipitemtext tooltipitemtext__right">{item.quantityType}</span>
            </td>
                <td className={"table__text--align table--col-m "  + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <input name="onOffhireDate" type={!item.reckonUpdatesRequired && !item.customerUpdatesRequired && item.allowEdit && !item.manualItem && scheduleDay.statusId === 1 ? 'date' : "hidden"} min={minDate} className="form-control schedulenew__date-size" value={item.onOffhireDate} onChange={(e) => onCellChange(e, false, jobIndex, item.itemIndex)} />
                {!item.reckonUpdatesRequired && !item.customerUpdatesRequired && item.allowEdit && scheduleDay.statusId === 1 ? "" : item.onOffhireDateFormatted}
            </td>
            <td className={"table__text--align table--col-s "  + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <div className="tooltipitem tooltipitem__right">{item.accountTypeId === 1 ? "COD" : item.accountTypeId === 2 ? "Acc" : item.accountTypeId === 3 ? "Acc PO" : ""}
                    <span className="tooltipitemtext tooltipitemtext__right">{item.accountType}</span>
                </div>
            </td>
            <td className="table__text--align table--col-s">
                
            </td>
            <td className="table__text--align table--col-s">
                { item.typeId === 1 ? item.jobAmountFormatted : "" }
            </td>
            <td className={"table__text--align table--col-s "  + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <input name="accountChecked" type='checkbox' className={(item.allowEdit && item.typeId === 1) ? "input" : "hidden"} checked={item.accountChecked} onChange={(e) => onCellCheckboxChange(e, false, jobIndex, item.itemIndex)} disabled={scheduleDay.statusId === 2 || !item.allowEdit} />
            </td>
            <td className={"table__text--align table--col-s " + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <input name="goAhead" type='checkbox' className={item.allowEdit ? "input" : "hidden"} checked={item.goAhead} onChange={(e) => onCellCheckboxChange(e, false, jobIndex, item.itemIndex)} disabled={scheduleDay.statusId === 2 || !item.allowEdit} />
            </td>
            <td className="table__text--align table--col-s allocate-notes--width">
                <a className={item.scheduleNotes == "" ? "hidden" : item.showRedNotes ? " makeitred" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, true, jobIndex, item.itemIndex)}>
                    <div className="tooltipitem tooltipitem__left">{item.scheduleNotes ? item.scheduleNotes.length < 60 ? item.scheduleNotes : item.scheduleNotes.substring(0, 60) + "... " : ""}
                        <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__left">{item.scheduleNotes}</span>
                    </div>
                </a>
                <a className={item.scheduleNotes != "" ? "hidden" : item.showRedNotes ? " makeitred" : item.showGreenNotes ? " makeitgreen" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, true, jobIndex, item.itemIndex)}>
                    <span className="fas fa-pencil-alt edit--icon"></span>
                </a>
            </td>
            <td className="table__text--align table--col-xs">
                <div className={(allowEdit || allowAllocateToPastDate) && item.allowEdit && scheduleDay.statusId === 1 ? "copy--tablecell" : "hidden"}>
                    <a href="#" onClick={(e) => copyItem(e, false, jobIndex, item.itemIndex)}>
                        <span className="fas fa-copy copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Roll over job to another day</span></span>
                    </a>
                </div>
            </td>
            <td className="table__text--align table--col-xs">
                <div className={(allowEdit || allowAllocateToPastDate) && item.allowDelete && scheduleDay.statusId === 1 ? "" : "hidden"}>
                    <a href="#" onClick={(e) => rescheduleItem(e, false, jobIndex, item.itemIndex)}>
                        <span className="fas fa-calendar-alt copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Reschedule job to another day</span></span>
                    </a>
                </div>
            </td>
            <td className="table__text--align table--col-xs">
                <div className={item.typeId === 1 && allowEdit && !item.reckonUpdatesRequired && !item.customerUpdatesRequired && item.allowEdit && item.allowInvoice && scheduleDay.statusId === 1 ? "" : "hidden"}>
                    <a href="#" onClick={(e) => invoiceItem(e, false, jobIndex, item.itemIndex)}>
                        <span className="fa-solid fa-file-invoice-dollar edit--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Create a Proforma Invoice for this job</span></span>
                    </a>
                </div>
            </td>
            <td className="table__text--align table--col-xs">
                {item.itemStatusId != 8 && scheduleDay.statusId === 1 && item.allowDelete ?
                    <div className="delete--tablecell">
                        <a href="#" onClick={(e) => cancelItem(e, false, jobIndex, item.itemIndex)}>
                            <span className="fas fa-times-circle edit--icon alignIconCenter makeitred tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Cancel Booking</span></span>
                        </a>
                    </div> : ""}
            </td>
        </tr>
    )
};

//JOBS
const SortableItem = SortableElement(({ jobIndex, job, scheduleDay, allowEdit, allowAllocateToPastDate, minDate, showInfo, onCellChange, onCellCheckboxChange, onCellJobCheckboxChange, editNotes, copyItem, rescheduleItem, cancelItem, showItems, invoiceItem }: { jobIndex: number, job: ScheduleJobs, scheduleDay: ScheduleDay, allowEdit: boolean, allowAllocateToPastDate: boolean, minDate: string, showInfo(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellChange(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellCheckboxChange(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellJobCheckboxChange(e: any, jobIndex: number): void, editNotes(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, copyItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, rescheduleItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, cancelItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, showItems(e: any, itemIndex: number, showItem: boolean): void, invoiceItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void }) =>
    <React.Fragment key={jobIndex}>
        <tr className={(!job.showItems || job.totalItems === 0) && job.childTypeId === 1 && job.isPlaceholder ? "tableColours--install-ph" : (!job.showItems || job.totalItems === 0) && job.childTypeId === 1 ? "tableColours--install" : (!job.showItems || job.totalItems === 0) && job.childTypeId === 2 && job.isPlaceholder ? "tableColours--pickup-ph" : (!job.showItems || job.totalItems === 0) && job.childTypeId === 2 ? "tableColours--pickup" : (!job.showItems || job.totalItems === 0) && job.childTypeId === 3 ? "tableColours--assist" : (!job.showItems || job.totalItems === 0) && job.childTypeId === 5 ? "tableColours--manual" : "" }>
            <td className="hidden">{job.id}</td>
            <td className={"table__text--align " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" :  "")}>
                <div className={job.totalItems > 0 ? "" : "hidden"}>
                    <a href="#" onClick={(e) => showItems(e, jobIndex, !job.showItems)}>
                        <span className={job.showItems ? "fas fa-times copy--icon alignIconCenter" : "fas fa-plus copy--icon alignIconCenter"}></span>
                    </a>
                </div>
                <div className={job.totalItems > 0 ? "hidden" : ""}>
                    {job.jobIndex}
                </div>
            </td>
            <td className={"table__text--align table--col-xs " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" :  "")}>
                <div className={job.totalItems > 0 ? "hidden" : ""}>
                    <a href="#" onClick={(e) => showInfo(e, true, jobIndex, job.itemIndex)}>
                        <span className="fas fa-info-circle copy--icon alignIconCenter tooltipitem tooltipitem__right"><span className="tooltipitemtext tooltipitemtext__right">Booking Information</span></span>
                    </a>
                </div>
            </td>
            <td className={"table__text--align table--col-s " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" :  "")}>
                <div >
                    <input name="orderManual" maxLength={50} type={scheduleDay.statusId === 1 ? 'text' : "hidden"} className="form-control schedulenew__text-sm" value={job.orderManual} onChange={(e) => onCellChange(e, true, jobIndex, job.itemIndex)} />
                    {scheduleDay.statusId === 1 ? "" : job.orderManual}
                </div>
            </td>
            <td className={"table__text--align"}>
                <Link className={job.jobId == 0 ? "hidden" : (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" : "")} to={'/job/' + job.jobId} target="_blank">
                    <div className="tooltipitem tooltipitem__right">{job.siteAddress.length > 30 ? job.siteAddress.substring(0, 30) + "... " : job.siteAddress}
                        <span className="tooltipitemtext tooltipitemtext__right">{job.siteAddress}</span>
                    </div>
                </Link>
                <div className={job.jobId != 0 ? "hidden" : "tooltipitem tooltipitem__right"}>{job.siteAddress ? job.siteAddress.substring(0, 20) + "... " : ""}
                    <span className="tooltipitemtext tooltipitemtext__right">{job.siteAddress}</span>
                </div>
            </td>
            <td className="table__text--align" >
                <Link className={job.customerId === 0 ? "hidden" : "table__text--align " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" : "")} to={'/customer/' + job.customerId} target="_blank">
                    <div className="tooltipitem tooltipitem__right">{job.customer.length > 15 ? job.customer.substring(0, 15) + "... " : job.customer}
                        <span className="tooltipitemtext tooltipitemtext__right">{job.customer}</span>
                    </div>
                </Link>
                {job.customerId === 0 ? job.customer : "" }
            </td>
            <td className={job.totalItems > 0 && !job.itemIsKwikstage && !job.itemIsVoid ? "hidden" : "table__text--align table--col-s " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <div className={job.totalItems > 0 && !job.itemIsKwikstage && !job.itemIsVoid ? "hidden" : ""}>
                    <div className="tooltipitem tooltipitem__right">{job.manualItem ? job.description.substring(0, 5) + "... " : job.totalItems > 0 && job.itemIsVoid ? "Void" : job.itemShortcut}
                        <span className="tooltipitemtext tooltipitemtext__right">{job.manualItem ? job.description : job.item}</span>
                    </div>
                </div>
            </td>
            <td className={job.totalItems > 0 && !job.itemIsKwikstage && !job.itemIsVoid ? "hidden" : "table__text--align table--cell--nowrap table--col-s " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <span className="tooltipitemtext tooltipitemtext__right">{job.totalItems > 0 ? job.itemQuantityType : job.quantityType}</span>
            </td>
            <td className={job.totalItems > 0 && !job.itemIsKwikstage && !job.itemIsVoid ? ("table__text--align " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" : "")) : "hidden"} colSpan={2}>
                <span>{job.itemQuantityType}</span>
            </td>
            <td className={"table__text--align table--col-m "  + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" :  "")}>
                <div className={job.totalItems > 0 ? "hidden" : ""}>
                    <input name="onOffhireDate" type={!job.reckonUpdatesRequired && !job.customerUpdatesRequired && job.allowEdit && !job.manualItem && scheduleDay.statusId === 1 ? 'date' : "hidden"} min={minDate} className="form-control schedulenew__date-size" value={job.onOffhireDate} onChange={(e) => onCellChange(e, true, jobIndex, job.itemIndex)} />
                    {!job.reckonUpdatesRequired && !job.customerUpdatesRequired && job.allowEdit && scheduleDay.statusId === 1 ? "" : job.onOffhireDateFormatted}
                </div>
            </td>
            <td className={"table__text--align table--col-s "  + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" :  "")}>
                <div className="tooltipitem tooltipitem__right">{job.accountTypeId === 1 ? "COD" : job.accountTypeId === 2 ? "Acc" : job.accountTypeId === 3 ? "Acc PO" : ""}
                    <span className="tooltipitemtext tooltipitemtext__right">{job.accountType}</span>
                </div>
            </td>
            <td className="table__text--align table--col-s">
                {job.typeId === 1 ? job.remainingBalanceFormatted : ""}
            </td>
            <td className="table__text--align table--col-s">
                {job.typeId === 1 ? job.jobAmountFormatted : ""}
            </td>
            <td className={"table__text--align table--col-s "  + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" :  "")}>
                <div>
                    <input name="accountChecked" type='checkbox' className={(job.allowEdit && job.typeId === 1) ? "input" : "hidden"} checked={job.accountChecked} onChange={(e) => onCellJobCheckboxChange(e, jobIndex)} disabled={scheduleDay.statusId === 2 || !job.allowEdit || (job.accountTypeId === 3 && !job.purchaseOrderNumber)} />
                    <div className={job.accountTypeId === 3 && job.typeId === 1 && !job.purchaseOrderNumber ? "tooltipitem tooltipitem__right" : "hidden"}>
                        <span className="fas fa-circle-exclamation makeitred edit--icon marginLeft10"></span>
                        <span className="tooltipitemtext tooltipitemtext__right">Enter Purchase Order Number!</span>
                    </div>
                </div>
            </td>
            <td className={"table__text--align table--col-s " + (job.reckonUpdatesRequired ? "allocate__customerReckonText" : job.customerUpdatesRequired ? "allocate__customerText" : "")}>
                <div>
                    <input name="goAhead" type='checkbox' className={job.allowEdit ? "input" : "hidden"} checked={job.goAhead} onChange={(e) => onCellJobCheckboxChange(e, jobIndex)} disabled={scheduleDay.statusId === 2 || !job.allowEdit} />
                </div>
            </td>
            <td className="table__text--align allocate-notes--width">
                <div className={job.totalItems > 0 ? "hidden" : "tooltipitem tooltipitem__right"}>
                    <a className={job.scheduleNotes == "" ? "hidden" : job.showRedNotes ? " makeitred" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, true, jobIndex, job.itemIndex)}>
                        <div className="tooltipitem tooltipitem__left">{job.scheduleNotes ? job.scheduleNotes.length < 60 ? job.scheduleNotes : job.scheduleNotes.substring(0, 60) + "... " : ""}
                            <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__left">{job.scheduleNotes}</span>
                        </div>
                    </a>
                    <a className={job.scheduleNotes != "" ? "hidden" : job.showRedNotes ? " makeitred" : job.showGreenNotes ? " makeitgreen" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, true, jobIndex, job.itemIndex)}>
                        <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                    </a>
                </div>
            </td>
            <td className="table__text--align table--col-xs">
                <div className={job.totalItems === 0 && (allowEdit || allowAllocateToPastDate) && job.allowEdit && scheduleDay.statusId === 1 ? "copy--tablecell" : "hidden"}>
                    <a href="#" onClick={(e) => copyItem(e, true, jobIndex, job.itemIndex)}>
                        <span className="fas fa-copy copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Roll over job to another day</span></span>
                    </a>
                </div>
            </td>
            <td className="table__text--align table--col-xs">
                <div className={job.totalItems === 0 && (allowEdit || allowAllocateToPastDate) && job.allowDelete && scheduleDay.statusId === 1 ? "" : "hidden"}>
                    <a href="#" onClick={(e) => rescheduleItem(e, true, jobIndex, job.itemIndex)}>
                        <span className="fas fa-calendar-alt copy--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Reschedule job to another day</span></span>
                    </a>
                </div>
            </td>
            <td className="table__text--align table--col-xs">
                <div className={job.childTypeId === 1 && job.allowEdit && job.allowInvoice && scheduleDay.statusId === 1 ? "" : "hidden"}>
                    <a href="#" onClick={(e) => invoiceItem(e, true, jobIndex, job.itemIndex)}>
                        <span className="fa-solid fa-file-invoice-dollar edit--icon alignIconCenter tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Create a Proforma Invoice for this job</span></span>
                    </a>
                </div>
            </td>
            <td className="table__text--align table--col-xs">
                {job.totalItems === 0 && job.itemStatusId != 8 && scheduleDay.statusId === 1 && job.allowDelete ?
                    <div className="delete--tablecell">
                        <a href="#" onClick={(e) => cancelItem(e, true, jobIndex, job.itemIndex)}>
                            <span className="fas fa-times-circle edit--icon alignIconCenter makeitred tooltipitem tooltipitem__left"><span className="tooltipitemtext tooltipitemtext__left">Cancel Booking</span></span>
                        </a>
                    </div> : ""
                }
            </td>
        </tr>
        <tr className={job.showItems ? "" : "hidden"}>
            <td className="schedulenew__padding-none" colSpan={18}>
                <table className='table subtable--nomargin'>
                    {job.items.map((item, index) =>
                        <tbody key={index}>
                            <ItemRow itemIndex={index} jobIndex={jobIndex} item={item} scheduleDay={scheduleDay} allowEdit={allowEdit} allowAllocateToPastDate={allowAllocateToPastDate} minDate={minDate} onCellChange={onCellChange} onCellCheckboxChange={onCellCheckboxChange} showInfo={showInfo} rescheduleItem={rescheduleItem} copyItem={copyItem} editNotes={editNotes} cancelItem={cancelItem} invoiceItem={invoiceItem} />
                        </tbody>
                    )}
                </table>

            </td>
        </tr>
    </React.Fragment>
);

const SortableList = SortableContainer(({ scheduleDay, allowEdit, allowAllocateToPastDate, minDate, showInfo, onCellChange, onCellCheckboxChange, onCellJobCheckboxChange, editNotes, copyItem, rescheduleItem, cancelItem, showItems, invoiceItem }: { scheduleDay: ScheduleDay, allowEdit: boolean, allowAllocateToPastDate: boolean, minDate: string, showInfo(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellChange(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellCheckboxChange(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, onCellJobCheckboxChange(e: any, jobIndex: number): void, editNotes(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, copyItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, rescheduleItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, cancelItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number): void, showItems(e: any, itemIndex: number, showItem: boolean): void, invoiceItem(e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) : void }) => {
    return (
        <tbody>
            {scheduleDay.jobs.map((job, index) =>
                <SortableItem key={`item-${index}`} index={index} jobIndex={index} job={job} scheduleDay={scheduleDay} allowEdit={allowEdit} allowAllocateToPastDate={allowAllocateToPastDate} minDate={minDate} onCellChange={onCellChange} onCellCheckboxChange={onCellCheckboxChange} onCellJobCheckboxChange={onCellJobCheckboxChange} showInfo={showInfo} rescheduleItem={rescheduleItem} copyItem={copyItem} editNotes={editNotes} cancelItem={cancelItem} showItems={showItems} invoiceItem={invoiceItem } />
            )}
        </tbody>
    );

});

export default class ScheduleDayData extends React.Component<ScheduleDayProps, ScheduleDayItem> {
    constructor(props: ScheduleDayProps) {
        super(props);

        this.state = {
            showAddNew: false,
            //install: [],
            //pickup: [],
            showDayNotes: false,
            editDayNotes: {
                heading: "",
                rowIndex: 0,
                parentIndex: 0,
                id: 0,
                item: "",
                itemId: 0,
                notes: ""
            },
            jobOnly: false,
            jobIndex: -1,
            itemIndex: -1,
            showNotes: false,
            showCopy: false,
            onOffhireDate: "",
            copyItem: {
                index: 0,
                scheduleId: 0,
                item: "",
                siteAddress: "",
                quantityScheduled: 0,
                qtyType: "",
                unitOfMeasure: "",
                isKwikstage: false,
                kwikstageItems: [],
                kwikstageOptions: []
            },
            jobs: [],
            kwikstageJobs: [],
            showReschedule: false,
            showHoliday: false,
            showCancel: false,
            cancelJobIndex: -1,
            cancelIndex: -1,
            showProformaInvoice: false,
            showJobInfo: false,
            showVoidGearList: false,
            scheduleId: -1,
            jobQuoteItemId: -1,
            showFlaggedJobs: false,
            errors: {},
            loading: false,
            loadingReckonData: false
        };


    }

    lock = () => {
        this.lockSchedule();
    }

    async lockSchedule() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let scheduleDay = this.props.scheduleDay;
        let scheduledDate = scheduleDay.scheduledDate;
        let statusId = scheduleDay.statusId === 1 ? 2 : 1;
        let status = scheduleDay.statusId === 1 ? "Locked" : "Open";

        let schedule = {
            scheduleDate: scheduledDate,
            scheduleBranchId: this.props.scheduleBranchId,
            statusId: statusId,
            subId: user.sub
        };

        axios.post('api/Schedule/LockSchedule', schedule, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            //reload the day
            toast.success("Schedule Locked");
            //update the schedule
            scheduleDay.statusId = statusId;
            scheduleDay.status = status;

            this.props.updateScheduleDay(scheduledDate, scheduleDay);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    handleChange = (e: any) => {
        var scheduleDay = this.props.scheduleDay;

        let change: any = scheduleDay;
        change[e.target.name] = e.target.value;

        this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, change);
    }

    onCellChange = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        var scheduleDay = this.props.scheduleDay;
        var job = scheduleDay.jobs[jobIndex];
        job.isDirty = true;

        if (jobOnly) {
            //job level
            let newItem: any = {};
            newItem = job;
            newItem[e.target.name] = e.target.value;
        } else {
            //job item level
            var item = job.items[itemIndex];
            let newItem: any = {};
            newItem = item;
            newItem[e.target.name] = e.target.value;
            newItem.isDirty = true;
        }
        this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
    }

    onCellCheckboxChange = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        var scheduleDay = this.props.scheduleDay;
        var job = scheduleDay.jobs[jobIndex];
        job.isDirty = true;

        if (jobOnly) {
            let newItem: any = {};
            newItem = job;
            newItem[e.target.name] = e.target.checked;
        } else {
            var item = job.items[itemIndex];
            let newItem: any = {};
            newItem = item;
            newItem[e.target.name] = e.target.checked;
            newItem.isDirty = true;
        }

        this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
    }

    onCellJobCheckboxChange = (e: any, jobIndex: number) => {
        var scheduleDay = this.props.scheduleDay;
        var job = scheduleDay.jobs[jobIndex];
        job.isDirty = true;

        let newItem: any = {};
        newItem = job;
        newItem[e.target.name] = e.target.checked;

        //if job has items then update all the items
        if (job.totalItems > 0) {
            var items = job.items;
            for (var i = 0; i < items.length; i++) {
                let newJobItem: any = {};
                newJobItem = items[i];
                newJobItem[e.target.name] = e.target.checked;
            }
        }

        this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
    }

    showItems = (e: any, jobIndex: number, showItem: boolean) => {
        e.preventDefault();

        var scheduleDay = this.props.scheduleDay;
        var job = scheduleDay.jobs[jobIndex];
        job.showItems = showItem;

        this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
    }

    showMap = () => {
        window.open("/schedulenew/map/1/" + this.props.scheduleDay.scheduledDate, "_blank");
    }

    saveLimits = () => {
        this.saveDayLimits();
    }

    async saveDayLimits() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var scheduleDay = this.props.scheduleDay;

        var limits = {
            scheduleDate: scheduleDay.scheduledDateActual,
            scheduleBranchId: this.props.scheduleBranchId,
            shortNotes: scheduleDay.shortNotes,
            walkWayGuardRailPitchMax: scheduleDay.walkWayGuardRailPitchMax,
            voidMax: scheduleDay.voidMax,
            kwikstageMax: scheduleDay.kwikstageMax,
            subId: user.sub
        };

        axios.post('api/Schedule/SaveDayLimits', limits, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Schedule Limits saved");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //DAY NOTES
    editDayNotes = (e: any) => {
        e.preventDefault();

        var dayNotes: EditDayNotes = {
            id: 0,
            heading: "Edit Notes for " + this.props.scheduleDay.scheduledDateFormattedSmall,
            item: "",
            itemId: 0,
            parentIndex: 0,
            rowIndex: 0,
            notes: this.props.scheduleDay.notes
        };

        this.setState({ showDayNotes: true, editDayNotes: dayNotes })
    }

    resetDayNote = () => {
        var dayNotes: EditDayNotes = {
            id: 0,
            heading: "",
            item: "",
            itemId: 0,
            parentIndex: 0,
            rowIndex: 0,
            notes: ""
        };
        return dayNotes;
    }

    hideDayNotes = () => {
        var dayNotes = this.resetDayNote();
        this.setState({ showDayNotes: false, editDayNotes: dayNotes })
    }

    updateDayNotes = (data: EditDayNotes) => {
        var editDayNotes = this.state.editDayNotes;
        editDayNotes.notes = data.notes;
        this.setState({
            editDayNotes: editDayNotes
        });
    }

    saveDayNotes = () => {
        this.dayNoteSave();
    }

    async dayNoteSave() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var scheduleDay = this.props.scheduleDay;
        scheduleDay.notes = this.state.editDayNotes.notes;

        let noteToSave = {
            scheduleDate: scheduleDay.scheduledDateActual,
            scheduleBranchId: this.props.scheduleBranchId,
            note: scheduleDay.notes,
            subId: user.sub
        };

        //save day notes 
        axios.post('api/Schedule/SaveDayNote', noteToSave, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                var dayNotes = this.resetDayNote();
                this.setState({ showDayNotes: false, editDayNotes: dayNotes })

                this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
                toast.success("Schedule Notes saved");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //END DAY NOTES

    //HOLIDAY
    hideHoliday = () => {
        this.setState({ showHoliday: false });
    }

    saveHoliday = () => {
        this.setState({ showAddNew: true, showHoliday: false });
    }

    //END HOLIDAY

    //RECKON ACCOUNT BALANCE
    getReckonBalance = () => {
        this.setState({ loadingReckonData: true });
        this.getReckonAccountBalance();
    }

    getReckonAccountBalance = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let scheduleJobs = this.props.scheduleDay;
        let accountBalanceJobs = scheduleJobs.jobs.filter(j => j.typeId === 1); //only need balance for installs

        var acctBalance = {
            customerAccountBalances: accountBalanceJobs,
            subId: user.sub
        };

        let dateToUpdate = scheduleJobs.scheduledDateActual;

        //save
        axios.post('api/Customer/GetReckonAccountBalance', acctBalance, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var jobsWithAcctBalance = res.data;
            for (var i = 0; i < jobsWithAcctBalance.length; i++) {
                var item = jobsWithAcctBalance[i];
                var schedJobToUpdate = scheduleJobs.jobs.find(j => j.id == item.id);
                if (schedJobToUpdate) {
                    schedJobToUpdate.accountBalance = item.accountBalance;
                    schedJobToUpdate.accountBalanceFormatted = item.accountBalanceFormatted;
                    schedJobToUpdate.creditLimit = item.creditLimit;
                    schedJobToUpdate.remainingBalance = item.remainingBalance;
                    schedJobToUpdate.remainingBalanceFormatted = item.remainingBalanceFormatted;
                }
            }
            this.setState({ loadingReckonData: false });

            this.props.updateScheduleDay(dateToUpdate, scheduleJobs);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END RECKON ACCOUNT BALANCE

    //FLAGGED JOBS
    showFlaggedJobs = () => {
        this.setState({ showFlaggedJobs: true });
    }

    hideFlaggedJobs = () => {
        this.setState({ showFlaggedJobs: false });
    }

    saveFlaggedJobs = (datesToUpdate: string[]) => {
        for (var i = 0; i < datesToUpdate.length; i++) {
            var dateToUpdate = datesToUpdate[i];
            this.props.updateDay(dateToUpdate, true);
        }
        //update current day
        this.props.updateDay(this.props.scheduleDay.scheduledDate, true);
        this.setState({ showFlaggedJobs: false });
        toast.success("Jobs Scheduled");
    }

    viewSearchDay = (scheduleDate: string, scheduleId: number) => {
        this.setState({ showFlaggedJobs: false });
        this.props.view(scheduleDate, scheduleId);
    }

    //END FLAGGED JOBS



    //ADD NEW
    add = () => {
        var isPublicHol = this.props.publicHolidays.filter(ph => ph.date == this.props.scheduleDay.scheduledDateActual).length > 0;
        var isShutdown = false;
        if (!isPublicHol) {
            //only check shutdown periods if it isn't a public holiday
            for (var i = 0; i < this.props.shutdownPeriods.length; i++) {
                var dateIsShutdown = DateHelpers.dateBetweenDates(this.props.scheduleDay.scheduledDate, this.props.shutdownPeriods[i].startDate, this.props.shutdownPeriods[i].endDate);
                if (dateIsShutdown) {
                    //if the date is in one shutdown period we don't need to check the rest
                    isShutdown = dateIsShutdown;
                    break;
                }
            }
        }
        if (isPublicHol || isShutdown) {
            //show warning
            this.setState({ showHoliday: true });
        } else {
            this.setState({ showAddNew: true });
        }
    }

    hideAddNew = () => {
        //this.setState({ showAddNew: false, install: [], pickup: [] });
        this.setState({
            showAddNew: false
        });
    }

    //updateAddNew = (scheduleItems: ScheduleItemDetails[], typeId: number) => {
    //    if (typeId === 1) {
    //        this.setState({ install: scheduleItems });
    //    } else {
    //        this.setState({ pickup: scheduleItems });
    //    }
    //}

    saveAddNew = (items: AddNewResults[]) => {
        this.addScheduleItem(items);
    }

    async addScheduleItem(items: AddNewResults[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var schedule = {
            scheduleDate: this.props.scheduleDay.scheduledDate,
            scheduleBranchId: this.props.scheduleBranchId,
            tiltTruckScheduler: false,
            scheduleItems: items,
            subId: user.sub
        };

        //save
        axios.post('api/Schedule/SaveAddNewScheduleItems', schedule, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            //reload the day
            toast.success("Schedule Saved");
            //this.setState({ showAddNew: false, install: [], pickup: [] });
            this.setState({ showAddNew: false });
            //update the other days that have had jobs moved
            var dates = res.data;
            for (var i = 0; i < dates.length; i++) {
                var dateToUpdate = dates[i];
                this.props.updateDay(dateToUpdate, true);
            }
            //update current day
            this.props.updateDay(this.props.scheduleDay.scheduledDate,true);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveManualJob = (newManualJob: NewItem) => {
        this.saveManualItem(newManualJob);
    }

    async saveManualItem(newManualJob: NewItem) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        newManualJob.scheduleBranchId = this.props.scheduleBranchId;
        newManualJob.subId = user.sub;

        axios.post('api/Allocate/SaveNewAllocationItem', newManualJob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Schedule Saved");
            //this.setState({ showAddNew: false, install: [], pickup: [] });
            this.setState({ showAddNew: false });
            this.props.updateDay(this.props.scheduleDay.scheduledDate, true);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END ADD NEW

    //COPY
    copyItem = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        e.preventDefault();

        //check if any items have changed
        let itemsChanged = this.props.scheduleDay.jobs.filter(x => x.isDirty);
        if (itemsChanged.length > 0) {
            if (this.validate()) {
                this.props.saveDay(this.props.scheduleDay.scheduledDate);
                this.showCopyItem(jobOnly, jobIndex, itemIndex);
            }
        } else {
            this.showCopyItem(jobOnly, jobIndex, itemIndex);
        }    
    }

    showCopyItem = (jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        var jobs = this.props.scheduleDay.jobs;
        var job = jobs[jobIndex];

        if (jobOnly) {
            if (job.itemIsKwikstage) {
                this.copyJobItem(jobOnly, jobIndex, itemIndex);
            } else {
                var copyDetails: CopyScheduleItem = {
                    index: jobIndex,
                    scheduleId: job.id,
                    siteAddress: job.siteAddress,
                    item: job.item,
                    quantityScheduled: job.quantityScheduled,
                    qtyType: job.quantityType,
                    unitOfMeasure: job.unitOfMeasure,
                    isKwikstage: false,
                    kwikstageItems: [],
                    kwikstageOptions: []
                };

                let jobs: JobItem[] = [];
                let jobItem: JobItem = {
                    itemIndex: 0,
                    quantity: 0,
                    scheduleDate: "",
                    warning: false,
                    locked: false
                };
                jobs.push(jobItem);

                this.setState({ onOffhireDate: job.onOffhireDate, jobs: jobs, kwikstageJobs: [], showCopy: true, copyItem: copyDetails });
            }
        } else {
            var itemToCopy = job.items[itemIndex];

            if (itemToCopy.itemIsKwikstage) {
                this.copyJobItem(jobOnly, jobIndex, itemIndex);
            } else {
                var copyDetails: CopyScheduleItem = {
                    index: itemIndex,
                    scheduleId: itemToCopy.id,
                    siteAddress: itemToCopy.siteAddress,
                    item: itemToCopy.item,
                    quantityScheduled: itemToCopy.quantityScheduled,
                    qtyType: itemToCopy.quantityType,
                    unitOfMeasure: itemToCopy.unitOfMeasure,
                    isKwikstage: false,
                    kwikstageItems: [],
                    kwikstageOptions: []
                };

                let jobs: JobItem[] = [];
                let jobItem: JobItem = {
                    itemIndex: 0,
                    quantity: 0,
                    scheduleDate: "",
                    warning: false,
                    locked: false
                };
                jobs.push(jobItem);

                this.setState({ onOffhireDate: itemToCopy.onOffhireDate, jobs: jobs, kwikstageJobs: [], showCopy: true, copyItem: copyDetails });
            }
        }
    }

    async copyJobItem(jobOnly: boolean, jobIndex: number, itemIndex: number) {
        const token = await authService.getAccessToken();
        var jobs = this.props.scheduleDay.jobs;
        var job = jobs[jobIndex];

        let itemToCopy = jobOnly ? job : job.items[itemIndex];
        
        //need to get some extra details for kwikstage items
        axios.get("api/Allocate/GetKwikstageRolloverDetails?ScheduleId=" + itemToCopy.id, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var ksItems = res.data.kwikstageRolloverItems;
            var ksOptions = res.data.kwikstageOptions;
            var totalBays = res.data.totalBays;

            var copyDetails: CopyScheduleItem = {
                index: jobOnly ? jobIndex : itemIndex,
                scheduleId: itemToCopy.id,
                siteAddress: itemToCopy.siteAddress,
                item: itemToCopy.item,
                quantityScheduled: totalBays,
                qtyType: itemToCopy.quantityType,
                unitOfMeasure: itemToCopy.unitOfMeasure,
                isKwikstage: itemToCopy.itemIsKwikstage,
                kwikstageItems: ksItems,
                kwikstageOptions: ksOptions
            };

            this.setState({ onOffhireDate: itemToCopy.onOffhireDate, kwikstageJobs: res.data.kwikstageJobs, jobs: [], showCopy: true, copyItem: copyDetails });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    hideCopy = () => {
        var copyDetails = this.resetCopyItem();
        this.setState({ showCopy: false, copyItem: copyDetails });
    }

    resetCopyItem = () => {
        var item: CopyScheduleItem = {
            index: 0,
            scheduleId: 0,
            siteAddress: "",
            item: "",
            quantityScheduled: 0,
            qtyType: "",
            unitOfMeasure: "",
            isKwikstage: false,
            kwikstageItems: [],
            kwikstageOptions: []
        };
        return item;
    }

    updateOnOffHireDate = (onOffHireDate: string) => {
        this.setState({ onOffhireDate: onOffHireDate });
    }

    updateCopyJobs = (jobs: JobItem[]) => {
        this.setState({ jobs: jobs });
    }

    updateCopyKwikstageJobs = (kwikstageJobs: KwikstageJobItem[]) => {
        this.setState({ kwikstageJobs: kwikstageJobs });
    }

    saveCopyItem = (saveItem: SaveItem) => {
        this.props.updateDay(this.props.scheduleDay.scheduledDate, true);

        //update the other days from the copied schedule
        for (var i = 0; i < saveItem.dates.length; i++) {
            var dateToUpdate = saveItem.dates[i];
            this.props.updateDay(dateToUpdate, true);
        }

        var copyDetails = this.resetCopyItem();
        this.setState({ showCopy: false, copyItem: copyDetails });
    }

    //END COPY

    //RESCHEDULE
    rescheduleItem = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        e.preventDefault();

        //check if any items have changed
        let itemsChanged = this.props.scheduleDay.jobs.filter(x => x.isDirty);
        if (itemsChanged.length > 0) {
            if (this.validate()) {
                this.props.saveDay(this.props.scheduleDay.scheduledDate);
                this.showReschedule(jobOnly, jobIndex, itemIndex);
            }
        } else {
            this.showReschedule(jobOnly, jobIndex, itemIndex);
        }
        
    }

    showReschedule = (jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        var jobs = this.props.scheduleDay.jobs;
        var job = jobs[jobIndex];

        if (jobOnly) {
            this.setState({ showReschedule: true, scheduleId: job.id });
        } else {
            var items = job.items;
            var itemToMove = items[itemIndex];
            this.setState({ showReschedule: true, scheduleId: itemToMove.id });
        }   
    }

    hideReschedule = () => {
        this.setState({ showReschedule: false, scheduleId: -1 });
    }

    saveRescheduleItem = (scheduleDateNew: string) => {
        //item has been saved but need to move the original item
        this.props.updateDay(this.props.scheduleDay.scheduledDate, true);
        this.props.updateDay(scheduleDateNew, true);
        this.setState({ showReschedule: false, scheduleId: -1 });
    }
    //END RESCHEDULE

    //CANCEL
    cancelItem = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        e.preventDefault();
        this.checkCancelItem(jobOnly, jobIndex, itemIndex);
    }

    async checkCancelItem(jobOnly: boolean, jobIndex: number, itemIndex: number) {
        const token = await authService.getAccessToken();

        var scheduleDay = this.props.scheduleDay;
        var jobs = scheduleDay.jobs;
        var job = jobs[jobIndex];
        let jobId = job.jobId;
        let schType = job.typeId;
        let itemId = job.itemId;

        if (!jobOnly) {
            var items = job.items;
            var itemToRemove = items[itemIndex];
            jobId = itemToRemove.jobId;
            schType = itemToRemove.typeId;
            itemId = itemToRemove.itemId;
        }

        //reset the status on the database and get the updated item back
        axios.get('api/Schedule/CheckCancelJob?JobId=' + jobId + '&itemId=' + itemId + '&ScheduleType=' + schType, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        }).then(res => {
            var canCancel = res.data;
            let errors: any = {};

            if (canCancel) {
                this.setState({ showCancel: true, jobOnly: jobOnly, cancelJobIndex: jobIndex, cancelIndex: itemIndex });
            } else {
                toast.error("Cannot cancel pickup job as customer cannot go into Overhire. Use Extend via Allocate screen instead.");
                errors["scheduleItems"] = "Cannot cancel pickup job as customer cannot go into Overhire. Use Extend via Allocate screen instead.";
                this.setState({ errors: errors });
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    hideCancel = () => {
        this.setState({ showCancel: false, jobOnly: false, cancelJobIndex: -1, cancelIndex: -1 });
    }

    saveCancel = () => {
        this.saveCancelItem();
    }

    async saveCancelItem() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var scheduleDay = this.props.scheduleDay;
        var jobs = scheduleDay.jobs;
        var job = jobs[this.state.cancelJobIndex];
        
        var scheduleId = job.id;

        if (!this.state.jobOnly) {
            var items = job.items;
            var itemToRemove = items[this.state.cancelIndex];
            scheduleId = itemToRemove.id;
        }

        //reset the status on the database and get the updated item back
        axios.get('api/Schedule/DeleteItem?ScheduleId=' + scheduleId + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ showCancel: false, jobOnly: false, cancelJobIndex: -1, cancelIndex: -1 });
            this.props.updateDay(this.props.scheduleDay.scheduledDate, true);

            //check if there is another date to update - happens if we rollover a job
            var dateToUpdate = res.data;
            if (dateToUpdate != "") {
                this.props.updateDay(dateToUpdate, true);
            }

            toast.success("Item has been unscheduled");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //END CANCEL

    //INVOICE JOB
    invoiceJob = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        e.preventDefault();
        this.setState({ showProformaInvoice: true, jobOnly: jobOnly, cancelJobIndex: jobIndex, cancelIndex: itemIndex });
    }

    hideProformaInvoice = () => {
        this.setState({ showProformaInvoice: false, jobOnly: false, cancelJobIndex: -1, cancelIndex: -1 });
    }

    saveProformaInvoice = () => {
        this.saveInvoice();
    }

    saveInvoice = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var scheduleDay = this.props.scheduleDay;
        var jobs = scheduleDay.jobs;
        var job = jobs[this.state.cancelJobIndex];

        //if they click on a job there could be multiple items to create an invoice for
        var scheduleIds: number[] = [];

        if (!this.state.jobOnly) {
            var items = job.items;
            var item = items[this.state.cancelIndex]
            item.allowInvoice = false;
            item.allowDelete = false;
            //only one job selected
            scheduleIds.push(item.id);
        } else {
            //check if there are items on the job and add them all
            var items = job.items;
            if (items.length > 0) {
                for (var i = 0; i < items.length; i++) {
                    scheduleIds.push(items[i].id);
                    items[i].allowInvoice = false;
                    items[i].allowDelete = false;
                }
            } else {
                //just add the one job schedule id
                job.allowInvoice = false;
                job.allowDelete = false;
                scheduleIds.push(job.id);
            }
        }

        var data = {
            scheduleIds: scheduleIds,
            jobId: job.jobId,
            subId: user.sub
        };

        axios.post('api/Invoicing/CreateProformaInvoice', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Proforma Invoice Created Successfully");
            this.setState({ showProformaInvoice: false, jobOnly: false, cancelJobIndex: -1, cancelIndex: -1 });

            this.props.updateDay(this.props.scheduleDay.scheduledDate, true);

        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //END INVOICE JOB

    //NOTES
    editNotes = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        e.preventDefault();

        var scheduleDay = this.props.scheduleDay;
        var scheduleJobs = scheduleDay.jobs;
        var scheduleJob = scheduleJobs[jobIndex];
        var scheduleId = -1;

        if (jobOnly) {
            scheduleId = scheduleJob.id;
        } else {
            var scheduleItems = scheduleJob.items;
            var scheduleItem = scheduleItems[itemIndex];
            scheduleId = scheduleItem.id;   
        } 

        this.setState({ jobOnly: jobOnly, scheduleId: scheduleId, jobIndex: jobIndex, itemIndex: itemIndex, showNotes: true });
    }

    hideNotesModal = () => {
        this.setState({ showNotes: false, jobOnly: false, scheduleId: -1, jobIndex: -1, itemIndex: -1, errors: {} });
    }

    saveNotes = (scheduleNotes: string, timestamp: Uint8Array) => {
        var scheduleDay = this.props.scheduleDay;
        var scheduleJobs = scheduleDay.jobs;
        var scheduleJob = scheduleJobs[this.state.jobIndex];

        if (this.state.jobOnly) {
            scheduleJob.scheduleNotes = scheduleNotes;
            scheduleJob.rowVersionTimestamp = timestamp;
            scheduleJob.showGreenNotes = scheduleNotes.length > 0;
        } else {
            var scheduleItems = scheduleJob.items;
            var scheduleItem = scheduleItems[this.state.itemIndex];
            scheduleItem.scheduleNotes = scheduleNotes;
            scheduleItem.rowVersionTimestamp = timestamp;
            scheduleItem.showGreenNotes = scheduleNotes.length > 0;
        }

        this.setState({ showNotes: false, jobOnly: false, scheduleId: -1, jobIndex: -1, itemIndex: -1, errors: {} });

        this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
    }

    //END NOTES

    //SHOW INFO
    showInfo = (e: any, jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        e.preventDefault();

        //check if any items have changed
        let itemsChanged = this.props.scheduleDay.jobs.filter(x => x.isDirty);
        if (itemsChanged.length > 0) {
            if (this.validate()) {
                this.props.saveDay(this.props.scheduleDay.scheduledDate);
                this.showInfoModal(jobOnly, jobIndex, itemIndex);
            }
        } else {
            this.showInfoModal(jobOnly, jobIndex, itemIndex);
        }
    }

    showInfoModal = (jobOnly: boolean, jobIndex: number, itemIndex: number) => {
        this.getJobInfo(jobOnly, jobIndex, itemIndex);
    }

    async getJobInfo(jobOnly: boolean, jobIndex: number, itemIndex: number) {
        var schedule = jobOnly ? this.props.scheduleDay.jobs[jobIndex] : this.props.scheduleDay.jobs[jobIndex].items[itemIndex];
        this.setState({ showJobInfo: true, jobOnly: jobOnly, jobIndex: jobIndex, itemIndex: itemIndex, scheduleId: schedule.id });
    }

    hideJobInfo = () => {
        this.setState({ showJobInfo: false, jobOnly: false, jobIndex: -1, itemIndex: -1, scheduleId: -1, jobQuoteItemId: -1 });
    }

    openGearlist = () => {
        var schedule = this.state.jobOnly ? this.props.scheduleDay.jobs[this.state.jobIndex] : this.props.scheduleDay.jobs[this.state.jobIndex].items[this.state.itemIndex];
        this.setState({ showJobInfo: false, showVoidGearList: true, jobQuoteItemId: schedule.jobQuoteItemId });
    }

    saveJobInfo = () => {
        this.setState({ showJobInfo: false, jobOnly: false, jobIndex: -1, itemIndex: -1, scheduleId: -1, jobQuoteItemId: -1 });
        this.props.updateDay(this.props.scheduleDay.scheduledDate, true);
    }
    //END SHOW INFO

    //VOID GEAR LIST
    hideVoidGearList = () => {
        this.setState({ showVoidGearList: false, showJobInfo: true, jobQuoteItemId: -1 });
    }

    saveVoidGearList = () => {
        this.setState({ showVoidGearList: false, showJobInfo: true, jobQuoteItemId: -1 });
    }
    //END VOID GEARLIST

    refreshDay = () => {
        this.setState({ errors: {} });
        this.props.updateDay(this.props.scheduleDay.scheduledDate, true);
    }

    private onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
        var scheduleDay = this.props.scheduleDay;
        var jobs = scheduleDay.jobs;

        if (oldIndex != newIndex) {
            jobs.splice(newIndex, 0, jobs.splice(oldIndex, 1)[0]);

            var items: ItemOrder[] = [];
            var jobOrder = 1;
            //loop through the items and update the order
            for (var j = 0; j < jobs.length; j++) {
                let job = jobs[j];
                job.itemIndex = j;
                if (job.totalItems === 0) {
                    job.jobOrder = jobOrder;
                    job.jobIndex = jobOrder;

                    let itemOrder: ItemOrder = {
                        scheduleId: job.id,
                        jobOrder: jobOrder,
                        rowVersionTimestamp: job.rowVersionTimestamp
                    };
                    items.push(itemOrder);
                    jobOrder += 1;
                } else {
                    job.jobIndex = jobOrder; //not used but can't be null
                    for (var i = 0; i < job.items.length; i++) {
                        let item = job.items[i];
                        item.itemIndex = i;
                        item.jobOrder = jobOrder;
                        item.jobIndex = jobOrder;
                        jobOrder += 1;

                        let itemOrder: ItemOrder = {
                            scheduleId: item.id,
                            jobOrder: item.jobOrder,
                            rowVersionTimestamp: item.rowVersionTimestamp
                        };
                        items.push(itemOrder);
                    }
                }
            }

            this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
            this.props.saveDayOrder(this.props.scheduleDay.scheduledDate, items);
        }
    };

    validate = () => {
        let errors: any = {};
        let formIsValid = true;

        //only validate items that have changed as those are the ones we are saving
        let scheduleDay = this.props.scheduleDay;
        var allItems = scheduleDay.jobs;
        let jobs = allItems.filter(x => x.isDirty);

        var errorString = "Please enter the following values: ";
        var error = false;
        var updateItems = false;

        for (var i = 0; i < jobs.length; i++) {
            for (var x = 0; x < jobs[i].items.length; x++) {
                var item = jobs[i].items[x];
                var newErrorString = "";

                //only validate items that are not manual items
                if (!item.manualItem) {
                    if (item.typeId === 1) {
                        if (item.onOffhireDate == "") {
                            newErrorString += " Onhire Date, ";
                            error = true;
                        } else if (!DateHelpers.isValidDate(item.onOffhireDate)) {
                            newErrorString += " Valid Onhire Date, ";
                            error = true;
                        } else if (!DateHelpers.compareDate(this.props.scheduleDay.scheduledDate, item.onOffhireDate)) {
                            //check to make sure offhire date is before or equal to pickup date
                            newErrorString += " Onhire Date must be >= Installation Date, ";
                            error = true;
                        }

                        //only check p/o if job is ready for allocating
                        if (item.accountTypeId == 3 && !item.purchaseOrderNumber.trim() && item.accountChecked && item.goAhead) {
                            newErrorString += " Purchase Order Number, ";
                            error = true;
                            item.goAhead = false;
                            updateItems = true;
                        }
                    } else {
                        if (item.onOffhireDate == "") {
                            newErrorString += " Offhire Date, ";
                            error = true;
                        } else if (!DateHelpers.isValidDate(item.onOffhireDate)) {
                            newErrorString += " Valid Offhire Date, ";
                            error = true;
                        } else if (!DateHelpers.compareDate(item.onOffhireDate, this.props.scheduleDay.scheduledDate)) {
                            //check to make sure offhire date is before or equal to pickup date
                            newErrorString += " Offhire Date must be <= Dismantle Date, ";
                            error = true;
                        }
                    }
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + (item.jobOrder) + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
            }
        }
        if (error) {
            formIsValid = false;
            errors["scheduleItems"] = errorString;
        }

        if (updateItems) {
            this.props.updateScheduleDay(this.props.scheduleDay.scheduledDate, scheduleDay);
        }
        
        this.setState({ errors: errors });
        return formIsValid;
    }

    save = () => {
        //validation
        if (this.validate()) {
            this.props.saveDay(this.props.scheduleDay.scheduledDate);
        } else {
            toast.error("Please fix validation errors and try Saving again!");
        }
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        var scheduleDay = this.props.scheduleDay;
        var minDate = this.props.allowAllocateToPastDate ? DateHelpers.addDaysToCurrentDate(-90) : (scheduleDay.scheduledDate < DateHelpers.addDaysToCurrentDate(-2)) ? scheduleDay.scheduledDate : DateHelpers.addDaysToCurrentDate(-2);
        var buttonText = scheduleDay.statusId == 1 ? "Lock" : "Unlock";
        let notesPopup = <NotesPopup showEditNotesModal={this.state.showDayNotes} editNotes={this.state.editDayNotes} hideNotes={this.hideDayNotes} updateNotes={this.updateDayNotes} saveNotes={this.saveDayNotes} />;
        let addNewSchedule = <AddNewScheduleData scheduleDate={scheduleDay.scheduledDate} dateFormatted={scheduleDay.scheduledDateFormatted} scheduleTypes={this.props.scheduleTypes} showModal={this.state.showAddNew} hideModal={this.hideAddNew} save={this.saveAddNew} saveManual={this.saveManualJob} />
        var notes = <EditNoteModal systemCompanyId={2} scheduleId={this.state.scheduleId} editDriverNotes={false} showDriverScheduleNotes={false} showEditNotesModal={this.state.showNotes} save={this.saveNotes} hideModal={this.hideNotesModal} />
        let copyJob = <CopyJobData onOffhireDate={this.state.onOffhireDate} scheduleBranchId={this.props.scheduleBranchId} jobs={this.state.jobs} kwikstageJobs={this.state.kwikstageJobs} showModal={this.state.showCopy} hideModal={this.hideCopy} copyItem={this.state.copyItem} updateOnOffHireDate={this.updateOnOffHireDate} updateJobs={this.updateCopyJobs} updateKwikstageJobs={this.updateCopyKwikstageJobs} saveModal={this.saveCopyItem} />
        let rescheduleJob = <RescheduleJobData publicHolidays={this.props.publicHolidays} shutdownPeriods={this.props.shutdownPeriods} reasonOptions={this.props.reasonOptions} showModal={this.state.showReschedule} hideModal={this.hideReschedule} scheduleId={this.state.scheduleId} saveModal={this.saveRescheduleItem} />
        let jobInfo = <JobInfoData systemCompanyId={2} scheduleId={this.state.scheduleId} showModal={this.state.showJobInfo} hideModal={this.hideJobInfo} openGearlist={this.openGearlist} saveModal={this.saveJobInfo} />
        let editVoidGearlist = <JobScheduleVoidModalData showModal={this.state.showVoidGearList} jobQuoteItemId={this.state.jobQuoteItemId} hideModal={this.hideVoidGearList} saveModal={this.saveVoidGearList} />
        let flaggedJobs = <FlaggedJobsData scheduleDate={scheduleDay.scheduledDate} dateFormatted={scheduleDay.scheduledDateFormatted} systemCompanyId={2} scheduleBranchId={this.props.scheduleBranchId} showModal={this.state.showFlaggedJobs} hideModal={this.hideFlaggedJobs} save={this.saveFlaggedJobs} filterDate={this.viewSearchDay} />
        let confirmHolPeriod = <ConfirmPopups heading="Public Holiday / Shutdown Period" text="Are you sure you want to add a job to a Public Holiday / Shutdown Period?" showConfirmModal={this.state.showHoliday} hideConfirmModal={this.hideHoliday} saveConfirmModal={this.saveHoliday} />
        let confirmCancel = <ConfirmPopups heading="Cancel Job" text="Are you sure you want to cancel this job?" showConfirmModal={this.state.showCancel} hideConfirmModal={this.hideCancel} saveConfirmModal={this.saveCancel} />
        let confirmInvoice = <ConfirmPopups heading="Create Proforma Invoice" text="Are you sure you want to create a proforma invoice for this job?" showConfirmModal={this.state.showProformaInvoice} hideConfirmModal={this.hideProformaInvoice} saveConfirmModal={this.saveProformaInvoice} />
        let showWarningBooked = scheduleDay.walkWayGuardRailPitchMaxRemaining + scheduleDay.voidMaxRemaining + scheduleDay.kwikstageMaxRemaining <= 0;
        let showWarningDayClose = scheduleDay.walkWayGuardRailPitchMaxRemaining < WalkWayGuardRailPitchWarningNo && scheduleDay.voidMaxRemaining < VoidWarningNo && scheduleDay.kwikstageMaxRemaining < KwikstageWarningNo;
        let warningText = showWarningBooked ? "Warning: Day booked out" : showWarningDayClose ? "Warning: Day close to being booked out" : "";
        let walkWayClass = scheduleDay.walkWayGuardRailPitchMaxRemaining <= 0 ? " makeitred makeitbold" : scheduleDay.walkWayGuardRailPitchMaxRemaining > 0 && scheduleDay.walkWayGuardRailPitchMaxRemaining < WalkWayGuardRailPitchWarningNo ? " makeitorange makeitbold" : "";
        let voidClass = scheduleDay.voidMaxRemaining <= 0 ? " makeitred makeitbold" : scheduleDay.voidMaxRemaining > 0 && scheduleDay.voidMaxRemaining < VoidWarningNo ? " makeitorange makeitbold" : "";
        let KwikstageClass = scheduleDay.kwikstageMaxRemaining <= 0 ? " makeitred makeitbold" : scheduleDay.kwikstageMaxRemaining > 0 && scheduleDay.kwikstageMaxRemaining < KwikstageWarningNo ? " makeitorange makeitbold" : "";

        return (
            <div className="schedulenew__day--marginside">
                <div>
                    {notesPopup}
                    {confirmHolPeriod}
                    {confirmCancel}
                    {confirmInvoice}
                    {addNewSchedule}
                    {copyJob}
                    {rescheduleJob}
                    {jobInfo}
                    {editVoidGearlist}
                    {flaggedJobs}
                    {notes}
                </div>
                <div className="schedulenew__statusBar">
                    <div>
                        <span className="schedulenew__day marginRight10">{scheduleDay.scheduledDateFormatted}</span>
                        <span className="marginLeft10">
                            <a className={scheduleDay.notes && scheduleDay.notes.length > 0 ? "makeitred" : ""} href="#" onClick={(e) => this.editDayNotes(e)}>
                                <span className="fas fa-pencil-alt edit--icon"></span>
                            </a>
                        </span>
                    </div>
                    <div><span className={scheduleDay.statusId === 1 ? "schedulenew__day" : "schedulenew__day makeitred"}>{ scheduleDay.status}</span></div>
                    <div>
                        <button className="defaultbutton defaultbutton__small--height marginLeft10" type='button' title="Refreshes the current day" onClick={this.refreshDay}>Refresh</button>
                        <button className={this.props.allowLock ? "defaultbutton defaultbutton__small--height marginLeft10" : "hidden"} type='button' title="Locks the current day so you cannot add or modify any jobs" onClick={this.lock}>{buttonText}</button>
                        <button className="defaultbutton defaultbutton__small--height marginLeft10" type='button' title="Shows the jobs on google maps" onClick={this.showMap}>Map</button>
                        <button className={this.props.allowGetAccountBalance ? "defaultbutton defaultbutton__medium--height marginLeft10" : "hidden"} type='button' title="Get customer account balance from Reckon" onClick={this.getReckonBalance}>Balance Check</button>
                        <button className="defaultbutton defaultbutton__small--height marginLeft10" type='button' title="Show all jobs with a bring forward date of today or earlier with a scheduled date > today" onClick={this.showFlaggedJobs} disabled={scheduleDay.statusId === 2}>Flagged</button>
                        <button className="defaultbutton defaultbutton__small--height marginLeft10" type='button' title="Allows user to add an existing quoted job, dismantle or manual job to the current day" onClick={this.add} disabled={scheduleDay.statusId === 2}>Add New</button>
                        <button className="defaultbutton defaultbutton__medium--height defaultbutton--green marginLeft10" type='button' onClick={this.save} disabled={scheduleDay.statusId === 2}>Save</button>
                    </div>
                </div>
                <div className="schedulenew__statusBar">
                    <div>
                        <label htmlFor="walkWayGuardRailPitchMax">
                            <span className="label label__right-padding">WW</span>
                            <input className="input input--xsmall marginRight10" type='number' id="walkWayGuardRailPitchMax" name="walkWayGuardRailPitchMax" value={scheduleDay.walkWayGuardRailPitchMax} onChange={(e) => this.handleChange(e)} disabled={!this.props.allowUpdateDayLimits}></input>
                            <span className={"label label__right-padding" + walkWayClass}>{scheduleDay.walkWayGuardRailPitchMaxRemaining}</span>
                        </label>
                        <label htmlFor="voidMax">
                            <span className="label label__right-padding">V</span>
                            <input className="input input--xsmall marginRight10" type='number' id="voidMax" name="voidMax" value={scheduleDay.voidMax} onChange={(e) => this.handleChange(e)} disabled={!this.props.allowUpdateDayLimits}></input>
                            <span className={"label label__right-padding" + voidClass}>{scheduleDay.voidMaxRemaining}</span>
                        </label>
                        <label htmlFor="kwikstageMax">
                            <span className="label label__right-padding">KS</span>
                            <input className="input input--xsmall marginRight10" type='number' id="kwikstageMax" name="kwikstageMax" value={scheduleDay.kwikstageMax} onChange={(e) => this.handleChange(e)} disabled={!this.props.allowUpdateDayLimits}></input>
                            <span className={"label label__right-padding" + KwikstageClass}>{scheduleDay.kwikstageMaxRemaining}</span>
                        </label>
                        <label htmlFor="shortNotes">
                            <input type='text' maxLength={200} id="shortNotes" name="shortNotes" className='input schedule__shortNote--size marginRight10' value={scheduleDay.shortNotes} onChange={(e) => this.handleChange(e)}></input>
                        </label>
                        <button className="defaultbutton defaultbutton__xsmall--height marginLeft10" type='button' title="Saves job limits for the day" onClick={this.saveLimits} disabled={scheduleDay.statusId === 2}>Save</button>
                    </div>
                    <div className="alignitemsbottom">
                        <div className={showWarningBooked ? "colAlignSplit warning-bkg--red paddingLeft10" : showWarningDayClose ? "colAlignSplit warning-bkg--orange paddingLeft10" : "colAlignSplit schedulewarning paddingLeft10"}>
                            <div>
                                <span className="warning-text">{warningText}</span>
                            </div>
                            <div>
                                <span>Light Duty Time: {scheduleDay.lightDutyMins}</span>
                                <span className="marginLeft10">Void Time: {scheduleDay.voidMins}</span>
                                <span className="marginLeft10">Kwikstage Total: {scheduleDay.kwikstageTotal}</span>
                            </div>
                            <div><span>Day Total: {scheduleDay.dayAmountFormatted}</span></div>
                        </div>
                    </div>

                </div>
                
                <div className={this.props.loadingDay || this.state.loadingReckonData ? "schedulenew-loadDay" : "hidden"}>
                    <h2>{this.props.loadingDay ? "Loading..." : "Retrieving Reckon Account Balances..."}</h2>
                </div>
                <div className={this.props.loadingDay || this.state.loadingReckonData ? "hidden" : ""}>
                    <div className="overflowAuto tableColours-header marginTop10">
                        <table className="table table__extralarge">
                            <thead>
                                <tr>
                                    <th className="hidden"></th>
                                    <th className="table--col-xs"></th>
                                    <th className="table--col-xs"></th>
                                    <th className="table--col-s">
                                        <div className="tooltipitem tooltipitem__right">Order
                                            <span className="tooltipitemtext tooltipitemtext__right">Temporary Allocation</span>
                                        </div>
                                    </th>
                                    <th>Site Address</th>
                                    <th>Customer</th>
                                    <th className="table--col-s">Item</th>
                                    <th className="table--col-s">Qty</th>
                                    <th className="table--col-m">On/Offhire Date</th>
                                    <th className="table--col-s">
                                        <div className="tooltipitem tooltipitem__right">Acct
                                            <span className="tooltipitemtext tooltipitemtext__right">Account Type - Prepaid, Account, Account Purchase Order</span>
                                        </div>
                                    </th>
                                    <th className="table--col-s">Acct. Bal.</th>
                                    <th className="table--col-s">Job Amt.</th>
                                    <th className="table--col-s">
                                        <div className="tooltipitem tooltipitem__right">Paid
                                            <span className="tooltipitemtext tooltipitemtext__right">Account is ok / Prepaid account paid</span>
                                        </div>
                                    </th>
                                    <th className="table--col-s">
                                        <div className="tooltipitem tooltipitem__right">Go/Ah
                                            <span className="tooltipitemtext tooltipitemtext__right">Go-ahead sign off recieved and ready for truck allocation</span>
                                        </div>
                                    </th>
                                    <th className="allocate-notes--width">
                                        <div className="tooltipitem tooltipitem__right">Notes
                                            <span className="tooltipitemtext tooltipitemtext__right">View/Add Notes to the job</span>
                                        </div>
                                    </th>
                                    <th className="table--col-xs">
                                        <div className="tooltipitem tooltipitem__left">R/O
                                            <span className="tooltipitemtext tooltipitemtext__left">Roll over job to another day</span>
                                        </div>
                                    </th>
                                    <th className="table--col-xs">Sch</th>
                                    <th className="table--col-xs">
                                        <div className="tooltipitem tooltipitem__left">Inv
                                            <span className="tooltipitemtext tooltipitemtext__left">Create a proforma invoice for the job</span>
                                        </div>
                                    </th>
                                    <th className="table--col-xs"></th>
                                </tr>
                            </thead>
                            <SortableList distance={1} onSortEnd={this.onSortEnd} scheduleDay={scheduleDay} allowEdit={this.props.allowEdit} allowAllocateToPastDate={this.props.allowAllocateToPastDate} minDate={minDate} onCellChange={this.onCellChange} onCellCheckboxChange={this.onCellCheckboxChange} onCellJobCheckboxChange={this.onCellJobCheckboxChange} showInfo={this.showInfo} rescheduleItem={this.rescheduleItem} copyItem={this.copyItem} editNotes={this.editNotes} cancelItem={this.cancelItem} showItems={this.showItems} invoiceItem={this.invoiceJob} />
                        </table>
                        {this.state.errors["scheduleItems"] ?
                            (this.state.errors["scheduleItems"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                        {scheduleDay.scheduleErrors.length > 0 ? scheduleDay.scheduleErrors.map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                    </div>
                </div>
            </div>
        )
    }
}
